import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import panelsCopy from '@/i18n/ns/artifact-panels';
import inspectCopy from '@/i18n/ns/inspect';
import settingsActionsCopy from '@/i18n/ns/settings-actions';
import { CompanyRequestError } from '@/company/api';
import { createInspectSession } from '../../../src/apps/selection/controller.mjs';
import type {
  InspectSession as DomainSession,
  TargetDetails,
} from '../../../src/apps/selection/controller.mjs';
import { createNativeInspectClient } from '../../../src/apps/selection/http-client.mjs';
import { InspectActionTicket } from '../../../src/apps/selection/host-contracts.mjs';
import type { InspectActionTicket as ActionTicket } from '../../../src/apps/selection/host-contracts.mjs';
import {
  createInspectSessionProjection,
  useInspectSessionProjection,
} from './view-models/use-inspect-session';
import type {
  InspectApplyView,
  InspectUndoView,
  InspectSessionProjection,
} from './view-models/inspect-session.mjs';
import { InspectToggle, MentionTray, presentationValueLabel } from '../inspect';
import { LoadingState, ErrorState } from '../primitives';
import {
  readNativeArtifactEditor,
  nativeInspectFetch,
  type NativeEditorCanvas as Editor,
} from './saved-canvas';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import type { SavedArtifactDisplay } from '../../../src/artifacts/saved-view-contract.mjs';
import type { ArtifactCanvasTarget, DraftCanvasTarget } from './routes';
import { AppSavedDisplay, registerSelectedAppHost } from '../apps/app-saved-display';
import { useSelectedAppHost } from '../apps/runtime/saved-app-host';
import { SavedArtifactDisplayView } from './document/document-view';
import { createSavedArtifactCanvasRenderer } from './saved-canvas-body';
import buildCopy from '@/i18n/ns/build-experience';
import type { SavedArtifactProvenance } from '../../../src/artifacts/saved-view-contract.mjs';
import { ArtifactFrame } from './artifact-frame';
import { useCanvasItemName, useCanvasKindLabel, type ArtifactCanvasRenderer } from './outlet';
import type { NativeSelectionAttachment } from '../types';

/** The meta line a saved version keeps: its kind, who can see it now, its saved ordinal and the
 * model it was built with. Each of the last facts comes from the saved view's own provenance and is
 * left out unless proven (never inferred, never from the URL, never a raw model id). */
function CurrentSavedDisplay(props: {
  display: SavedArtifactDisplay;
  number: number;
  target: ArtifactCanvasTarget | DraftCanvasTarget;
  companyId: string;
  actorId: string;
  provenance?: SavedArtifactProvenance;
  /** The view's own quiet actions (Edit), on the meta row. */
  actions?: ReactNode;
  onSharingChanged?: () => void;
}) {
  const { display, number, provenance, actions } = props;
  const locale = useChatInterfaceLocale();
  const panels = useNamespaceCopy(panelsCopy);
  const build = useNamespaceCopy(buildCopy);
  // The label is the server's canonical one for THIS version's own model, so a model that is no
  // longer offered still shows; there is no raw-id fallback.
  const model = provenance?.model?.displayLabel;
  const kind =
    display.kind === 'chart'
      ? panels.kindChart
      : display.kind === 'table'
        ? panels.kindTable
        : display.kind === 'deck'
          ? panels.kindDeck
          : display.kind === 'spreadsheet'
            ? panels.kindSpreadsheet
            : display.kind === 'report'
              ? panels.kindReport
              : panels.kindDocument;
  // The header names the loaded KIND only, in the interface language; the page carries the title.
  useCanvasKindLabel(display.kind === 'app' ? null : kind);
  useCanvasItemName((display as { title?: string }).title);
  if (display.kind === 'app') return <AppSavedDisplay {...props} display={display} />;
  const meta = [
    kind,
    ...(provenance?.audience === 'private' ? [panels.savedPrivate] : []),
    formatCopy(panels.savedVersion, { n: number }),
    ...(model ? [formatCopy(build.builtWith, { model })] : []),
  ];
  return (
    <>
      <div className="ap-saved-row">
        <p className="ap-saved-meta">{meta.join(' · ')}</p>
        {actions}
      </div>
      <SavedArtifactDisplayView display={display} locale={locale} />
    </>
  );
}

// Registered once, before the first saved App renders: the selected App's host (its own runtime frame).
registerSelectedAppHost(useSelectedAppHost);

const SavedBody = createSavedArtifactCanvasRenderer(CurrentSavedDisplay, AppSavedDisplay);

/** The sole host binding for an Artifact canvas target. A saved version: strict public display,
 * current interface locale, fetch/current authority and original identity in the canonical body.
 * Retained private editor drafts; retired interview/build drafts stay unavailable. */
export const SavedArtifactCanvasRenderer: ArtifactCanvasRenderer = ({ target, ...scope }) =>
  target.kind === 'artifact-draft' && target.tab === 'editor' ? (
    <NativeEditorCanvas
      key={JSON.stringify([scope.companyId, scope.actorId, target.id])}
      draftId={target.id}
      {...scope}
    />
  ) : target.kind === 'artifact-draft' ? (
    <RetiredDraft />
  ) : (
    <SavedBody target={target} {...scope} />
  );

function RetiredDraft() {
  const copy = useNamespaceCopy(panelsCopy);
  return <ErrorState message={copy.exportUnavailable} />;
}

type EditorRuntime = {
  domain: DomainSession;
  facade: InspectSessionProjection;
  bootstrap: Awaited<ReturnType<ReturnType<typeof createNativeInspectClient>['bootstrap']>>;
};
type Outcome = {
  action: 'apply' | 'undo';
  operationId: string;
  result: InspectApplyView | InspectUndoView;
};

function ticketClient(ticket: ActionTicket) {
  const { sessionId, artifactId, revisionId, buildId } = ticket;
  return createNativeInspectClient({
    binding: { sessionId, artifactId, revisionId, buildId },
    fetch: nativeInspectFetch,
  });
}
function retainedTicket(key: string): ActionTicket | undefined {
  const value = sessionStorage.getItem(key);
  return value === null ? undefined : InspectActionTicket.parse(JSON.parse(value));
}

/** The real draft locator is separate from every immutable saved C9 target. */
function NativeEditorCanvas({
  draftId,
  companyId,
  actorId,
  revealComposer,
}: {
  draftId: string;
  companyId: string;
  actorId: string;
  revealComposer?: (selection: NativeSelectionAttachment) => void;
}) {
  const recoveryKey = JSON.stringify(['poneva.inspect.original', companyId, actorId, draftId]);
  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState<{ attempt: number; editor?: Editor; error?: string } | null>(
    null,
  );
  useEffect(() => {
    let current = true;
    void Promise.resolve()
      .then(() => readNativeArtifactEditor(draftId, retainedTicket(recoveryKey)))
      .then((editor) => {
        if (current) setLoaded({ attempt, editor });
      })
      .catch((error) => {
        if (current)
          setLoaded({
            attempt,
            error:
              error instanceof CompanyRequestError
                ? error.message
                : new CompanyRequestError(503, 'native_inspect_unavailable').message,
          });
      });
    return () => {
      current = false;
    };
  }, [draftId, recoveryKey, attempt]);
  const reload = () => setAttempt((n) => n + 1);
  if (loaded?.attempt !== attempt) return <LoadingState />;
  if (!loaded.editor) return <ErrorState message={loaded.error!} onRetry={reload} />;
  return (
    <NativeEditorDisplay
      key={loaded.editor.binding.sessionId}
      editor={loaded.editor}
      recoveryKey={recoveryKey}
      reload={reload}
      revealComposer={revealComposer}
    />
  );
}

/** One admitted document, one INS controller and ART's existing projection. */
function NativeEditorDisplay({
  editor,
  recoveryKey,
  reload,
  revealComposer,
}: {
  editor: Editor;
  recoveryKey: string;
  reload: () => void;
  revealComposer?: (selection: NativeSelectionAttachment) => void;
}) {
  const locale = useChatInterfaceLocale();
  const copy = useNamespaceCopy(panelsCopy);
  const inspect = useNamespaceCopy(inspectCopy);
  // The same rule for the private editor: the loaded kind, never the title.
  useCanvasKindLabel(editor.display.kind === 'document' ? copy.kindDocument : copy.kindReport);
  const [runtime, setRuntime] = useState<EditorRuntime | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let current = true;
    let domain: DomainSession | null = null;
    const load = async () => {
      const client = createNativeInspectClient({
        binding: editor.binding,
        fetch: nativeInspectFetch,
      });
      const bootstrap = await client.bootstrap();
      // Translate only registered native node labels; identity and ancestry remain INS's.
      const nodeLabels: Record<string, string> = {
        'text-run': inspect.text,
        paragraph: inspect.inspectNodeParagraph,
        heading: inspect.inspectNodeHeading,
        list: inspect.inspectNodeList,
        table: copy.kindTable,
        document: copy.kindDocument,
      };
      const label = (pid: string) => {
        const raw = bootstrap.view.label(pid);
        return raw === null ? null : (nodeLabels[raw] ?? raw);
      };
      const view = {
        ...bootstrap.view,
        label,
        list: () =>
          bootstrap.view
            .list()
            .map((item) => ({ ...item, label: label(item.targetId) ?? item.label })),
      };
      if (!current) return;
      domain = createInspectSession({
        binding: editor.binding,
        api: client.api,
        guard: client.guard,
      });
      const value = (
        value: TargetDetails['slots'][number]['value'],
        slot: TargetDetails['slots'][number],
      ) => {
        if (typeof value === 'object') return inspect.numberFormat;
        return presentationValueLabel(slot.property, value, inspect);
      };
      const facade = createInspectSessionProjection({
        domain,
        view,
        subjectLabel: editor.display.title,
        canEdit: (snapshot) => snapshot.state !== 'unavailable',
        labels: { unavailable: inspect.stale, slot: (slot) => inspect[slot.property], value },
        describeTarget: (target) => ({
          context: (() => {
            const text = target.details?.slots.find((slot) => slot.property === 'text')?.value;
            if (typeof text !== 'string') return null;
            const preview = Array.from(text.trim().replace(/\s+/gu, ' '));
            return preview.slice(0, 44).join('') + (preview.length > 44 ? '…' : '');
          })(),
          where: editor.display.title,
          scopeLabel: formatCopy(copy.editingDraftOf, { number: editor.number }),
        }),
        issueHostIntent: async (ticket) => {
          const intent = await ticketClient(ticket).issueHostIntent(ticket);
          // A journal locator only, never the host intent, pixels or an authority grant.
          sessionStorage.setItem(recoveryKey, JSON.stringify(InspectActionTicket.parse(ticket)));
          return intent;
        },
        readCommitReceipt: (ticket) => ticketClient(ticket).readCommitReceipt(ticket),
        originalUndo: (ticket, input) => ticketClient(ticket).originalUndo(ticket, input),
        createOperationId: () => crypto.randomUUID(),
        describePrepared: (_operation, changes) => {
          const slots = domain!
            .getSnapshot()
            .targets.flatMap((target) => target.details?.slots ?? []);
          const before: string[] = [],
            after: string[] = [];
          for (const change of changes) {
            const slot = slots.find((slot) => slot.slotId === change.slotId);
            if (!slot) throw new TypeError('selection_unavailable');
            if (
              typeof change.oldValue === 'object' ||
              (!('action' in change) && typeof change.newValue === 'object')
            )
              throw new TypeError('number_format_preview_unavailable');
            before.push(value(change.oldValue, slot));
            after.push(
              value(
                'action' in change ? slot.presentation!.effectiveDefault : change.newValue,
                slot,
              ),
            );
          }
          return {
            before: before.join(' · '),
            after: after.join(' · '),
            scopeLabel: formatCopy(copy.editingDraftOf, { number: editor.number }),
          };
        },
      });
      if (current) setRuntime({ domain, facade, bootstrap: { ...bootstrap, view } });
    };
    void load().catch(() => {
      if (current) setError(true);
    });
    return () => {
      current = false;
      void domain?.dispose().catch(() => {});
    };
  }, [editor, recoveryKey, copy, inspect]);
  if (error) return <ErrorState message={inspect.failed} onRetry={reload} />;
  if (!runtime) return <LoadingState />;
  return (
    <CurrentPrivateDisplay
      editor={editor}
      runtime={runtime}
      reload={reload}
      locale={locale}
      revealComposer={revealComposer}
    />
  );
}

function CurrentPrivateDisplay({
  editor,
  runtime,
  reload,
  locale,
  revealComposer,
}: {
  editor: Editor;
  runtime: EditorRuntime;
  reload: () => void;
  locale: string;
  revealComposer?: (selection: NativeSelectionAttachment) => void;
}) {
  const copy = useNamespaceCopy(panelsCopy);
  const inspect = useNamespaceCopy(inspectCopy);
  const settings = useNamespaceCopy(settingsActionsCopy);
  const current = useInspectSessionProjection(runtime.facade);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [busy, setBusy] = useState(false);
  const selectionKey = current.targets
    .filter((target) => target.status === 'current')
    .map((target) => target.targetId)
    .join('|');
  useEffect(() => {
    if (selectionKey)
      setOutcome((previous) => (previous?.result.status === 'saved' ? null : previous));
  }, [selectionKey]);
  const [composer, setComposer] = useState<Element | null>(null);
  const [mentionRequested, setMentionRequested] = useState(false);
  useEffect(() => {
    if (!mentionRequested) return;
    const visibleComposer = () => {
      const roots = document.querySelectorAll<HTMLElement>(
        '.ws-chat-slot:not([hidden]) .aui-composer-root',
      );
      for (const root of roots) {
        if (root.closest('[hidden]') || !root.getClientRects().length) continue;
        const style = getComputedStyle(root);
        if (style.display === 'none' || style.visibility === 'hidden') continue;
        const input = root.querySelector<HTMLElement>('textarea, [contenteditable="true"]');
        if (!input || input.hasAttribute('disabled')) continue;
        // The chips sit inside the composer card, above the input, where attachments sit.
        setComposer(root.querySelector('[data-slot="aui_composer-mention-chips"]') ?? root);
        input.focus();
        setMentionRequested(false);
        return;
      }
    };
    const observer = new MutationObserver(visibleComposer);
    observer.observe(document.body, { subtree: true, childList: true, attributes: true });
    visibleComposer();
    const timeout = setTimeout(() => setMentionRequested(false), 3000);
    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [mentionRequested]);
  const [phone, setPhone] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const resize = () => setPhone(window.innerWidth < 640);
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);
  useEffect(() => {
    if (!editor.ticket) return;
    let current = true;
    const ticket = editor.ticket;
    void runtime.facade.restoreOriginal(ticket).then((result) => {
      if (current) setOutcome({ action: ticket.action, operationId: ticket.operationId, result });
    });
    return () => {
      current = false;
    };
  }, [editor.ticket, runtime]);
  const session = useMemo(
    () => ({
      ...current,
      apply: async (operationId: string) => {
        const result = await runtime.facade.actions.apply(operationId);
        if (result.status === 'saved') reload();
        return result;
      },
      undo: async () => {
        const result = await runtime.facade.actions.undo();
        if (result.status === 'saved') reload();
        return result;
      },
    }),
    [current, runtime, reload, editor.ticket],
  );
  const reconcile = async (undo: boolean) => {
    if (!outcome || busy) return;
    setBusy(true);
    const result =
      undo || outcome.action === 'undo'
        ? await session.undo()
        : await session.apply(outcome.operationId);
    if (result.status === 'saved') reload();
    else
      setOutcome({
        ...outcome,
        action: undo || outcome.action === 'undo' ? 'undo' : 'apply',
        result,
      });
    setBusy(false);
  };
  const mention = () => {
    if (!session.targets.length) return;
    revealComposer?.({
      capture: () => {
        const freeze = runtime.facade.captureSelection();
        return freeze
          ? async (destination) => {
              const result = await freeze(destination);
              await runtime.facade.actions.setOn(false);
              setComposer(null);
              return result;
            }
          : null;
      },
    });
    setMentionRequested(true);
  };
  return (
    <div className="ap-saved-chrome">
      <div className="ap-saved-row">
        <p className="ap-saved-meta" data-testid="artifact-editor-meta">
          {formatCopy(copy.editingDraftOf, { number: editor.number })}
          {outcome?.action === 'apply' && outcome.result.status === 'saved'
            ? ` · ${copy.versionCausePerson}`
            : ''}
        </p>
        {/* The same one Inspect toggle, on the meta row; the frame keeps the session, map, view and layer. */}
        <InspectToggle session={session} phone={phone} />
      </div>
      {outcome && (
        <div
          className="pc-inspect-original-status"
          role="status"
          aria-live="polite"
          data-testid="inspect-original-outcome"
        >
          {outcome.result.status === 'saved' ? (
            <>
              <span>{outcome.action === 'apply' ? copy.appliedDone : settings.undone}</span>
              {outcome.action === 'apply' && (
                <button
                  type="button"
                  className="pc-btn"
                  data-variant="quiet"
                  disabled={busy}
                  onClick={() => void reconcile(true)}
                >
                  {inspect.undo}
                </button>
              )}
            </>
          ) : outcome.result.reasonKey === 'receipt_reconciliation_required' ? (
            <>
              <span>{inspect.saveUncertain}</span>
              <button
                type="button"
                className="pc-btn"
                data-variant="quiet"
                disabled={busy}
                onClick={() => void reconcile(false)}
              >
                {inspect.checkAgain}
              </button>
            </>
          ) : (
            <span>
              {outcome.result.reasonKey === 'not_authorized'
                ? inspect.viewOnly
                : outcome.action === 'undo'
                  ? outcome.result.status === 'conflict' ||
                    outcome.result.reasonKey === 'undo_unavailable' ||
                    outcome.result.reasonKey === 'undo_conflict'
                    ? inspect.undoConflict
                    : inspect.undoFailed
                  : inspect.failed}
            </span>
          )}
        </div>
      )}
      <ArtifactFrame
        kind={editor.display.kind}
        heading={null}
        inspect={{
          session,
          nativeMap: runtime.bootstrap.nativeMap,
          view: runtime.bootstrap.view,
          phone,
          toggle: null,
          onMention: mention,
          onHandoff: mention,
        }}
      >
        <SavedArtifactDisplayView display={editor.display} locale={locale} />
      </ArtifactFrame>
      {composer && createPortal(<MentionTray session={session} phone={phone} />, composer)}
    </div>
  );
}
